import { isEqual } from 'lodash';

/**
 * Checks which properties have mixed (different) values across multiple items in an array.
 * Useful for bulk edit forms to determine which fields should be marked as "mixed".
 *
 * @param items - Array of items to compare
 * @param excludeProperties - Properties to exclude from comparison (e.g., 'id')
 * @returns Set of property names that have different values across items
 *
 * @note Only checks top-level properties. Nested objects are compared as whole objects,
 * not their individual properties.
 * @todo Add support for deep/nested property comparison (e.g., 'interval.start', 'address.city')
 */
export function getPropertiesWithMixedValues<T extends object>(
  items: T[],
  excludeProperties: Array<keyof T> = [],
): Set<keyof T> {
  if (items.length < 2) return new Set();

  const propertiesToCheck = (Object.keys(items[0]) as Array<keyof T>).filter(
    (property) => !excludeProperties.includes(property),
  );

  const mixedProperties = new Set<keyof T>();

  propertiesToCheck.forEach((property) => {
    const firstValue = items[0][property];

    // Check if all items have the same value using deep equality
    const hasMixedValues = items.some(
      (item) => !isEqual(item[property], firstValue),
    );

    if (hasMixedValues) {
      mixedProperties.add(property);
    }
  });

  return mixedProperties;
}
